import { create } from 'zustand';

import { PCD_CONST } from '@/constant';
import type {
  PCDData,
  PcdFileCacheStatus,
  PcdFileStoreActions,
  PcdFileStoreState,
} from '@/type';
import { createAsyncProcessQueue, logger } from '@/util';

import {
  getPcdPath as _getPcdPath,
  getPcdPathMap as _getPcdPathMap,
  getPcdFileStatus,
  getPcdFileStatusMapByLocal,
  getPcdInCache,
  readFileByWorker,
  removeAllPcdInCache,
  removePcdInCache,
  setPcdFileStatus,
  setPcdFileStatusMap,
  splitByCachedPcdFileList,
} from './util';

const INIT_STATE: PcdFileStoreState = {
  path: '',
  pcdFileFetchable: true,
};

const usePcdFileStore = create<PcdFileStoreState & PcdFileStoreActions>(
  (set, get) => {
    const getPcdPath = (fileName: string) => _getPcdPath(get().path, fileName);
    const getPcdPathMap = () => _getPcdPathMap(get().path);
    const fileFetchingQueue = createAsyncProcessQueue<
      () => Promise<PCDData & { size: number }>
    >({ concurrency: 5 });

    return {
      ...INIT_STATE,

      clear() {
        get().setPath('');
        get().clearFetchPcdQueue();
      },

      clearFetchPcdQueue() {
        fileFetchingQueue.clearQueue();
      },

      async fetchPcd(fileName) {
        if (fileName == null) {
          return Promise.reject(new Error(`\`fileName\` is empty`));
        }

        const pcdPath = getPcdPath(fileName);
        const status = await getPcdFileStatus(`${pcdPath}`);

        if (status.loaded && status.size > 0)
          return await get().getPcd(fileName);

        const { path } = pcdPath;

        try {
          const status = { isLoading: false, loaded: false, size: 0 };

          await setPcdFileStatus(path, fileName, status);

          const fetchFile = (
            pcdPath: string,
          ): Promise<PCDData & { size: number }> => {
            return new Promise((resolve, reject) => {
              fileFetchingQueue.push({
                key: pcdPath,
                action: () => readFileByWorker(pcdPath),
                onSuccess: resolve,
                onFail: reject,
              });
            });
          };

          const { size } = await fetchFile(`${pcdPath}`);

          if (!size) throw new Error('Invalid pcd file');

          status.loaded = true;
          status.size = size / 2 ** 20;

          await setPcdFileStatus(path, fileName, status);

          return await get().getPcd(fileName);
        } catch (error) {
          console.group(
            '[usePcdFileStore.fetchPcd] Error path: %s fileName: %s',
            path,
            fileName,
          );
          console.warn(error);
          console.groupEnd();

          return PCD_CONST.EMPTY_PCD_DATA;
        }
      },

      async fetchPcdAll() {
        const pcdFileMap = await getPcdPathMap();
        const pcdFileList = Object.entries(pcdFileMap).reduce((acc, cur) => {
          acc[+cur[0]] = cur[1];

          return acc;
        }, [] as string[]);
        const chunkSize = 10;
        const chunkLength = Math.ceil(pcdFileList.length / chunkSize);

        for (let i = 0; i < chunkLength; i++) {
          if (!get().pcdFileFetchable) break;

          const startIndex = i * chunkSize;

          await get().fetchPcdRange(startIndex, chunkSize);
        }
      },

      fetchPcdAt(index) {
        return getPcdPathMap()
          .then(pcdFileMap => pcdFileMap[index])
          .then(get().fetchPcd);
      },

      async fetchPcdList(pcdFileList) {
        get().setPcdFileFetchable(true);

        const { path } = get();
        const pcdDataList = [] as PCDData[];
        const splitedFileList = await splitByCachedPcdFileList(
          path,
          pcdFileList,
          get().pcdFileFetchable,
        );
        const pcdFileStatusMap = await getPcdFileStatusMapByLocal(path);

        for (let i = 0; i < splitedFileList.length; i++) {
          if (!get().pcdFileFetchable) break;

          const fileList = splitedFileList[i];

          if (typeof fileList === 'string') {
            pcdDataList.push(await get().fetchPcd(fileList));
            continue;
          }

          try {
            const readFilesEntryList = await Promise.all(
              fileList
                .filter(fileName => {
                  const status = pcdFileStatusMap[fileName];

                  return !status?.isLoading;
                })
                .map(async fileName => {
                  const key = `${path}/${fileName}`;
                  const status = pcdFileStatusMap[fileName];

                  if (status && (status.loaded || status.size > 0)) {
                    return get()
                      .getPcd(fileName)
                      .then(pcdData => ({ key, fileName, pcdData, size: -1 }));
                  } else {
                    const pcdPath = `${getPcdPath(fileName)}`;
                    const fetchFile = (
                      pcdPath: string,
                    ): Promise<PCDData & { size: number }> => {
                      return new Promise((resolve, reject) => {
                        fileFetchingQueue.push({
                          key: pcdPath,
                          action: () => readFileByWorker(pcdPath),
                          onSuccess: resolve,
                          onFail: reject,
                        });
                      });
                    };

                    return fetchFile(pcdPath).then(({ size, ...pcdData }) => ({
                      key,
                      fileName,
                      pcdData,
                      size: size ? size / 2 ** 20 : 0,
                    }));
                  }
                }),
            );

            if (
              !get().pcdFileFetchable ||
              !readFilesEntryList.every(({ key }) => !!key.match(get().path))
            )
              throw new Error(
                `path 가 변경되어 download 를 중단합니다. pcd files path: ${readFilesEntryList[0].key}, current path: ${path}`,
              );

            const statusEntryList = [] as [string, PcdFileCacheStatus][];
            const entryList = [] as [string, PCDData][];

            readFilesEntryList.forEach(({ key, pcdData, size }) => {
              const pcdPath = key.replace(/^\//, '');

              if (size != -1) {
                statusEntryList.push([
                  pcdPath,
                  { isLoading: false, loaded: true, size, optimized: false },
                ]);
              }

              entryList.push([pcdPath, pcdData]);
            });

            await setPcdFileStatusMap(path, statusEntryList);

            pcdDataList.push(...entryList.map(v => v[1]));
          } catch (error: any) {
            console.group('[usePcdFileStore.fetchPcdList] Error');
            console.warn(error);
            console.groupEnd();

            get().setPcdFileFetchable(false);

            fileList.forEach(fileName => {
              setPcdFileStatus(path, fileName, {
                isLoading: false,
                loaded: false,
                size: 0,
              });
            });

            break;
          }
        }

        return pcdDataList;
      },

      async fetchPcdRange(startIndex, limit = 10) {
        return getPcdPathMap().then(pcdFileMap => {
          const pcdFileList = Object.entries(pcdFileMap).reduce((acc, cur) => {
            acc[+cur[0]] = cur[1];

            return acc;
          }, [] as string[]);

          return get().fetchPcdList(
            pcdFileList.slice(
              startIndex,
              Math.min(startIndex + limit, pcdFileList.length),
            ),
          );
        });
      },

      getPcd(fileName) {
        return logger.performance(
          () => getPcdInCache(`${getPcdPath(fileName)}`),
          `getPcd-idb-${fileName}`,
        );
      },

      getPcdAt(index) {
        return getPcdPathMap()
          .then(pcdFileMap => pcdFileMap[index])
          .then(get().getPcd);
      },

      removeAllPcd() {
        return removeAllPcdInCache(get().path).then(() => {});
      },

      removePcd(fileName) {
        return removePcdInCache(`${getPcdPath(fileName)}`);
      },

      removePcdAt(index) {
        return getPcdPathMap()
          .then(pcdFileMap => pcdFileMap[index])
          .then(get().removePcd);
      },

      setPath(path) {
        if (path && path === get().path) return;

        set({ path });
        get().setPcdFileFetchable(false);

        getPcdFileStatusMapByLocal(path);
      },

      setPcdFileFetchable(pcdFileFetchable: boolean) {
        set({ pcdFileFetchable });
      },
    };
  },
);

export default usePcdFileStore;
